import React from 'react';
import {
  Box,
  Typography,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import { CreateInvoiceData } from '@/models/invoice';
import { Customer } from '@/models/customer';
import './SmartSummary.css';

export interface SmartSummaryProps {
  formData: CreateInvoiceData;
  selectedCustomer: Customer | null;
  onRefresh: () => void;
  disabled?: boolean;
  loading?: boolean;
}

const SmartSummary: React.FC<SmartSummaryProps> = ({
  formData,
  selectedCustomer,
  onRefresh,
  disabled = false,
  loading = false
}) => {
  // Format currency function
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <Box className="smart-summary">
      {/* Header */}
      <Box className="ss-summary-header">
        <Box className="ss-header-left">
          <Typography variant="h6" className="ss-summary-title">
            Financial Summary
          </Typography>
        </Box>
        <Box className="ss-header-actions">
          {/* Future: Add summary actions if needed */}
        </Box>
      </Box>

      {/* Content */}
      <Box className="ss-summary-content">
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220 }}>
            <CircularProgress />
          </Box>
        ) : formData.bill_amount > 0 ? (
          <Box className="ss-summary-items">
            {/* Bill Amount */}
            <Box className="ss-summary-item">
              <Typography variant="caption" className="ss-summary-label">
                Bill Amount
              </Typography>
              <Typography variant="body2" className="ss-summary-value">
                {formatCurrency(formData.bill_amount)}
              </Typography>
            </Box>

            {/* Discount */}
            {formData.discount > 0 && (
              <Box className="ss-summary-item">
                <Typography variant="caption" className="ss-summary-label">
                  Discount ({formData.discount}%)
                </Typography>
                <Typography variant="body2" className="ss-summary-value discount">
                  -{formatCurrency((formData.bill_amount * formData.discount) / 100)}
                </Typography>
              </Box>
            )}

            {/* Gross Amount */}
            <Box className="ss-summary-item">
              <Typography variant="caption" className="ss-summary-label">
                Gross Amount
              </Typography>
              <Typography variant="body2" className="ss-summary-value">
                {formatCurrency(formData.gross_amount)}
              </Typography>
            </Box>

            {/* Packing Charges */}
            {formData.packing_charges > 0 && (
              <Box className="ss-summary-item">
                <Typography variant="caption" className="ss-summary-label">
                  Packing
                </Typography>
                <Typography variant="body2" className="ss-summary-value">
                  {formatCurrency(formData.packing_charges)}
                </Typography>
              </Box>
            )}

            {/* GST Breakdown */}
            {(formData.cgst > 0 || formData.sgst > 0 || formData.igst > 0) && (
              <>
                {formData.cgst > 0 && (
                  <Box className="ss-summary-item">
                    <Typography variant="caption" className="ss-summary-label">
                      CGST
                    </Typography>
                    <Typography variant="body2" className="ss-summary-value gst">
                      {formatCurrency(formData.cgst)}
                    </Typography>
                  </Box>
                )}

                {formData.sgst > 0 && (
                  <Box className="ss-summary-item">
                    <Typography variant="caption" className="ss-summary-label">
                      SGST
                    </Typography>
                    <Typography variant="body2" className="ss-summary-value gst">
                      {formatCurrency(formData.sgst)}
                    </Typography>
                  </Box>
                )}

                {formData.igst > 0 && (
                  <Box className="ss-summary-item">
                    <Typography variant="caption" className="ss-summary-label">
                      IGST
                    </Typography>
                    <Typography variant="body2" className="ss-summary-value gst">
                      {formatCurrency(formData.igst)}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Round Off */}
            {formData.round_off !== 0 && (
              <Box className="ss-summary-item">
                <Typography variant="caption" className="ss-summary-label">
                  Round Off
                </Typography>
                <Typography variant="body2" className="ss-summary-value roundoff">
                  {formatCurrency(formData.round_off)}
                </Typography>
              </Box>
            )}

            <Divider className="ss-summary-divider" />

            {/* Final Total */}
            <Box className="ss-summary-item total">
              <Typography variant="body2" className="ss-summary-label total">
                Total Amount
              </Typography>
              <Typography variant="h6" className="ss-summary-value total">
                {formatCurrency(formData.total_amount)}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box className="ss-empty-state">
            <CalculateIcon className="ss-empty-icon" />
            <Typography variant="caption" className="ss-empty-text">
              Add products to see summary
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SmartSummary; 